import { FC } from "react";
import { Notification } from "../../../types/global/types";
import { useTranslation } from "../../../hooks/useTranslation";
import "./styles.scss";

type Props = {
  notification: Notification;
};

/**
 * Fix round 1 (volunteering-join report): the backend now persists a
 * VolunteerOffersCancelledNotification whenever a volunteer's own address
 * move auto-cancels one or more out-of-area offers
 * (volunteer-move-notice-report.md) -- the ONLY place he ever learned this
 * before was the save's own toast, which is gone the moment he navigates
 * away. Read-only, like RouteDivisionCompletedNotification/
 * RouteDivisionFailedNotification: no REACTIONS, no confirm/dismiss
 * affordance, nothing to act on here.
 *
 * This renders for volunteer-only accounts too (no workspace-admin
 * permissions) -- it reads only `notification` props, no redux selectors
 * gated on role/permission, so there is nothing here that could behave
 * differently for that account type.
 */
export const VolunteerOffersCancelledNotification: FC<Props> = ({ notification }) => {
  const { p } = useTranslation("notifications");
  const tp = notification.title_params;
  const notes = notification.notes as any;
  const centres: { id: number; name: string }[] =
    typeof notes === "object" && notes !== null && Array.isArray(notes.centres) ? notes.centres : [];

  // Same singular/plural, `title_params`-driven sentence NotificationItem's
  // list row builds -- kept in sync deliberately rather than re-deriving it
  // differently here. Falls back to the backend's own English sentence
  // (`notification.title`) only if `title_params` is ever missing, same
  // defensive shape every other type in this switch already uses.
  //
  // Fix round 2: guard non-empty too, not just truthy -- not currently
  // reachable (the backend only ever creates this notification when at
  // least one centre was cancelled), but `tp.centres[0]` on an empty array
  // is `undefined`, which would otherwise render as a blank name rather
  // than falling back to the backend's own sentence.
  const count = tp?.count ?? centres.length;
  const title =
    tp && tp.centres && tp.centres.length > 0
      ? count === 1
        ? p("nt_volunteer_offer_removed", { name: tp.centres[0] })
        : p("nt_volunteer_offers_removed", { count, names: tp.centres.join(", ") })
      : `${notification.title?.startOfTitle ?? ""}${notification.title?.householdName ?? ""}${
          notification.title?.endOfTitle ?? ""
        }`;

  return (
    <div className="notification">
      <div className="notification__header">
        <div className="notification__title">
          <h3>{title}</h3>
          <div className="notification__time">{notification.created_at_formatted}</div>
        </div>
      </div>

      {centres.length > 0 && (
        <div className="notification__notes">
          <div className="notification__notes-header">{p("nt_volunteer_offers_removed_list_header")}</div>
          <div className="notification__notes-body">
            <ul style={{ margin: 0, paddingInlineStart: "1.2em" }}>
              {centres.map((centre) => (
                <li key={centre.id}>{centre.name}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
